// v10/src/components/ItemCallout.js
// Callout tipo globo de diálogo que aparece encima del ítem activo.
// Muestra botón de imagen de referencia y/o sugerencia.
// Sin timer — se cierra al tildar el ítem o con la X.
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Lightbulb, X, ChevronRight } from 'lucide-react';

const ItemCallout = ({
  item,           // objeto del ítem (con .text, .referenceImage?, .suggestion?)
  darkMode,
  onShowImage,    // callback para abrir ReferenceImageModal
  onDismiss,      // callback para cerrar el callout
}) => {
  const hasImage      = !!item?.referenceImage;
  const hasSuggestion = !!item?.suggestion;

  // Si no tiene ni imagen ni sugerencia, no renderizar
  if (!hasImage && !hasSuggestion) return null;

  const suggestion = item?.suggestion || null;

  return (
    <AnimatePresence>
      <motion.div
        key="callout"
        initial={{ opacity: 0, y: 12, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        style={{ position: 'relative', marginBottom: '6px', zIndex: 10 }}
      >
        {/* ── Burbuja ───────────────────────────────────────── */}
        <div style={{
          borderRadius: '16px',
          background: darkMode ? 'rgba(14,20,38,0.98)' : 'rgba(255,255,255,0.98)',
          border: darkMode
            ? '1px solid rgba(99,130,246,0.22)'
            : '1px solid rgba(59,130,246,0.18)',
          boxShadow: darkMode
            ? '0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(99,130,246,0.08)'
            : '0 6px 28px rgba(59,130,246,0.12)',
          overflow: 'hidden',
        }}>
          {/* Accent strip */}
          <div style={{
            height: '3px',
            background: 'linear-gradient(90deg, #3b82f6, #7c3aed)',
          }} />

          <div style={{ padding: '12px 14px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasImage || hasSuggestion ? '10px' : 0 }}>
              <span style={{
                fontSize: '10px', fontWeight: 800, textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: darkMode ? 'rgba(147,197,253,0.8)' : '#2563eb',
              }}>
                ✈ Asistente
              </span>
              <motion.button
                onClick={onDismiss} whileTap={{ scale: 0.85 }}
                style={{
                  width: '22px', height: '22px', borderRadius: '7px', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
                  color: darkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)',
                }}
              >
                <X style={{ width: '11px', height: '11px' }} />
              </motion.button>
            </div>

            {/* Botón imagen */}
            {hasImage && (
              <motion.button
                onClick={onShowImage}
                whileHover={{ scale: 1.02, x: 2 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px', borderRadius: '12px', border: 'none',
                  cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  marginBottom: hasSuggestion ? '8px' : 0,
                  background: darkMode
                    ? 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(109,40,217,0.12))'
                    : 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(109,40,217,0.06))',
                  border: darkMode
                    ? '1px solid rgba(59,130,246,0.2)'
                    : '1px solid rgba(59,130,246,0.15)',
                }}
              >
                <div style={{
                  width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #3b82f6, #7c3aed)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Camera style={{ width: '15px', height: '15px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{
                    margin: 0, fontSize: '13px', fontWeight: 700,
                    color: darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(15,25,55,0.9)',
                  }}>
                    Ver imagen de referencia
                  </p>
                  <p style={{
                    margin: 0, fontSize: '11px',
                    color: darkMode ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                  }}>
                    Tocá para ampliar
                  </p>
                </div>
                <ChevronRight style={{ width: '14px', height: '14px', color: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)', flexShrink: 0 }} />
              </motion.button>
            )}

            {/* Sugerencia — siempre visible si existe */}
            {hasSuggestion && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '10px 12px', borderRadius: '12px',
                background: darkMode
                  ? 'rgba(245,158,11,0.08)'
                  : 'rgba(245,158,11,0.07)',
                border: darkMode
                  ? '1px solid rgba(245,158,11,0.18)'
                  : '1px solid rgba(245,158,11,0.2)',
              }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '9px', flexShrink: 0,
                  background: 'rgba(245,158,11,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(245,158,11,0.25)',
                }}>
                  <Lightbulb style={{ width: '13px', height: '13px', color: '#f59e0b' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: '0 0 2px', fontSize: '10px', fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    color: darkMode ? 'rgba(251,191,36,0.8)' : '#b45309',
                  }}>
                    Sugerencia
                  </p>
                  <p style={{
                    margin: 0, fontSize: '12.5px', fontWeight: 500, lineHeight: 1.45,
                    color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(15,25,55,0.78)',
                  }}>
                    {suggestion.text}
                  </p>
                  {suggestion.detail && (
                    <p style={{
                      margin: '4px 0 0', fontSize: '11.5px', lineHeight: 1.4,
                      color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.38)',
                    }}>
                      {suggestion.detail}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Colita del globo (apunta hacia abajo-izquierda, al ícono del ítem) ── */}
        <div style={{ position: 'relative', height: '12px' }}>
          {/* Borde de la colita */}
          <div style={{
            position: 'absolute', left: '28px', top: 0,
            width: 0, height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderTop: darkMode
              ? '12px solid rgba(99,130,246,0.22)'
              : '12px solid rgba(59,130,246,0.18)',
          }} />
          {/* Relleno de la colita */}
          <div style={{
            position: 'absolute', left: '29px', top: 0,
            width: 0, height: 0,
            borderLeft: '9px solid transparent',
            borderRight: '9px solid transparent',
            borderTop: darkMode
              ? '11px solid rgba(14,20,38,0.98)'
              : '11px solid rgba(255,255,255,0.98)',
          }} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ItemCallout;
